import { useEffect, useRef, useState } from 'react';
import { requestCancelToken, useService } from 'hooks/useService';
import { general_error_toastr } from 'components/partials/Notification';
import debounce from 'helpers/debounce';
import SelectWithFilter from 'components/partials/Fields/FormikSelectWithFilter/index.jsx';
import { getIn, useFormikContext } from 'formik';
import { useGlobalContext } from 'context/GlobalContext';
import { APP_CONFIG } from 'Config';

const AddressSelect = ({
    initialSearch,
    name = 'address',
    countryField,
    onKeyDown,
    setLoadingTimezones,
    ...props
}) => {
    const { values, setFieldValue, errors, setErrors, validateForm } = useFormikContext();
    const { countries } = useGlobalContext();
    const [country, setCountry] = useState(null);
    const country_id = getIn(values, countryField);
    const isInitialRender = useRef(true);
    const triggerEmptyInitialSearch = useRef(true);
    const triggerInitialSearch = useRef(true);
    const isEdit = window.location.href.includes('edit');
    const [checkLoading, setCheckLoading] = useState(false);
    const [_, fireGetTimezoneByAddress] = useService();

    useEffect(() => {
        if (isInitialRender.current && (Object.keys(errors).length > 0) && !isEdit) {
            const cleaned = { ...errors };
            delete cleaned[name];
            setErrors(cleaned);
            isInitialRender.current = false;
        }
    }, [errors]);

    useEffect(() => {
        if (countries) {
            const country = countries.find(c => c.country_id == country_id);
            setCountry(country);
        }
    }, [countries, country_id]);

    const [{ data: { results = [] }, loading, setData }, fireSearchAddress] = useService();

    const [cancelCallback, setCancelCallback] = useState(null);

    const handleTermChange = async (searchTerm, callback, getOption, searchProps = {}, selected, addressId) => {
        if (cancelCallback) {
            await cancelCallback('canceled-new-call');
        }

        const { cancelToken, cancelCallback: requestCancel } = await requestCancelToken();
        await setCancelCallback(() => requestCancel);

        const baseParams = {
            search: searchTerm,
            ...searchProps,
            ...(selected ? { selected } : {}),
        };

        const params = country_id == 37
            ? { ...baseParams, address_id: addressId }
            : baseParams;

        try {
            setCheckLoading(true);
            const { results: addresses = [] } = await fireSearchAddress({
                url: `users/check/address/${country?.country_code.toLowerCase() || 'us'}`,
                params: params,
                cancelToken,
            });

            if (callback) {
                callback(addresses.map(getOption));
            }

            setCheckLoading(false);

        } catch (e) {
            return general_error_toastr();
        }
    };

    const handleTermChangeDebounced = debounce(handleTermChange, 700);

    const handleSearchWithValidation = (searchTerm, callback, getOption, searchProps = {}, selected, addressId) => {
        if (!searchTerm || searchTerm.length < 5) {
            if (callback) {
                callback([]);
            }
            return;
        }

        handleTermChangeDebounced(searchTerm, callback, getOption, searchProps, selected, addressId);
    };

    const handleGetOption = (option) => {
        const {
            street,
            city,
            zipcode,
            state,
            entries,
            secondary,
            address_text,
        } = option;

        let mainAddress = street;

        if (entries === 1 && secondary) {
            mainAddress += ` ${secondary}`;
        }

        const additionalAddressParts = [];
        if (city) additionalAddressParts.push(city);
        if (state) additionalAddressParts.push(state);
        if (zipcode) additionalAddressParts.push(zipcode);

        const addressLabel = additionalAddressParts.length
            ? `${mainAddress}, ${additionalAddressParts.join(', ')}`
            : mainAddress;

        if (country_id == 37) {
            if ('zipcode' in option && option?.zipcode?.trim() !== '') {
                setCanadianFieldValues(option);
                setData([]);
            }
        }

        return ({
            value: country_id == 37 ? address_text : addressLabel,
            option,
            label: country_id == 37 ? `${address_text} ${entries > 0 ? '(' + entries + ')' : ''}` : `${addressLabel} ${entries > 0 ? '(' + entries + ')' : ''}`,
        });
    };

    const setCanadianFieldValues = (option) => {
        const { city, zipcode, state } = option;
        const getState = country?.states.find(s => s.state_code == state);
        const getTimezone = country?.timezones.find(t => t.timezone == getState?.timezone);

        setFieldValue('city', city);
        setFieldValue('zipcode', zipcode);
        setFieldValue('state_id', getState?.state_id || '');
        setFieldValue('timezone_id', getTimezone?.timezone_id || '');
    };

    const getTimezoneByAddress = async option => {
        setLoadingTimezones(true);
        try {
            const { timezone } = await fireGetTimezoneByAddress({
                url: `users/check/address/timezone/${country.country_code}`,
                params: {
                    street: option.street,
                    city: option.city,
                    state: option.state,
                    zipcode: option.zipcode,
                },
            });

            return timezone.timezone_id;
        } catch (e) {
            general_error_toastr();
            console.error(e);
        } finally {
            setLoadingTimezones(false);
        }
    };

    const handleChange = async ({ target: { value, option } }) => {
        if (option) {
            const { city, zipcode, state, entries, secondary, address_id, address_text } = option;
            const getState = country?.states.find(s => s.state_code == state);
            const getTimezone = country?.timezones.find(t => t.timezone == getState?.timezone);

            setFieldValue('city', city);
            setFieldValue('zipcode', zipcode);
            setFieldValue('state_id', getState?.state_id ?? '');

            const timezone = APP_CONFIG.multipleTimezoneStates.includes(getState?.state_code)
                ? await getTimezoneByAddress(option)
                : getTimezone?.timezone_id;

            setFieldValue('timezone_id', timezone ?? '');

            if (entries > 0) {
                const selected = country_id == 37 ? address_text : `${value.split(',')[0]} ${secondary} (${entries}) ${city} ${state} ${zipcode}`;
                const search_term = country_id == 37 ? address_text : `${value.split(',')[0]} ${city} ${state} ${zipcode}`;
                handleTermChange(search_term, null, handleGetOption, {}, selected, address_id);
            }
        }
        setFieldValue(name, value.split(',')[0]);
    };

    useEffect(() => {
        if (!initialSearch && triggerEmptyInitialSearch.current) {
            handleTermChangeDebounced(initialSearch, null, handleGetOption);
            triggerEmptyInitialSearch.current = false;
            if (!isEdit) {
                triggerInitialSearch.current = false;
            }
        } else if (triggerInitialSearch.current) {
            setFieldValue(name, initialSearch);
            handleTermChangeDebounced(initialSearch, null, handleGetOption, { search_type: 'address' });
            triggerInitialSearch.current = false;
        }
    }, [initialSearch]);

    useEffect(() => {
        validateForm();
    }, [values.city, values.state_id, values.zipcode, values.timezone_id]);

    return (
        <SelectWithFilter
            onKeyDown={onKeyDown}
            loading={loading}
            classNamePrefix="two-lines"
            onTermChange={handleSearchWithValidation}
            getOption={handleGetOption}
            label="Address"
            name={name}
            options={results}
            onChange={handleChange}
            {...props}
        />
    );
};

export default AddressSelect;
